import { useDispatch, useSelector } from "react-redux";
import {
  setActiveSession,
  setError,
  setLoading,
  startCashCount,
  stopCashCount,
  toggleCashCount,
  setOpeningRegister,
  setOpeningAmount,
  setOpeningDetails,
  resetOpeningForm,
  clearSession
} from "../../store/slices/cash/cashSessionSlice";

import { CashRegisterSessionOpenPayload, CashOpeningDetail } from "../../types/cash/session";
import CashSessionService from "../../services/cash/CashSessionService";
import { RootState } from "../../store/store";
import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";
import { usePrintService } from "../printing/usePrintService";

export const useCashSession = () => {

  const dispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const {
    activeSession,
    loading,
    error,
    isCountingCash,
    openingForm
  } = useSelector(
    (state: RootState) => state.cashSession
  );

  const { requestLegacyPrint } = usePrintService();


  /**
 * 📊 Obtener estado de caja de la terminal
 */
  const getSessionStatus = async (registerId: number) => {

    try {

      showLoadingIndicator();
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await CashSessionService.getSessionStatus(registerId);

      const { statusCode, data } = response;

      if (statusCode === 200) {
        dispatch(setActiveSession(data));
      }

    } catch (error) {
      handle(error);
    } finally {

      hideLoadingIndicator();
      dispatch(setLoading(false));

    }

  };


  /**
   * 🔓 Abrir sesión de caja
   */
  const openSession = async (payload: CashRegisterSessionOpenPayload) => {

    try {
      showLoadingIndicator();
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await CashSessionService.openSession(payload);

      console.log(response);

      const { statusCode, type, title, message, data } = response;

      if (statusCode === 200) {

        dispatch(setActiveSession(data));

        dispatch(resetOpeningForm());

        showAlert(type, title, message);

        void requestLegacyPrint('CASH_OPENING', data.id).catch(() => undefined);
      }

    } catch (error) {
      console.log(error);
      handle(error);
    } finally {
      hideLoadingIndicator();
      dispatch(setLoading(false));

    }

  };


  const submitOpeningSession = async () => {
    if (!openingForm.registerId) {
      dispatch(setError("Seleccione una terminal POS antes de abrir caja"));
      return;
    }

    dispatch(setError(null));

    const payload: CashRegisterSessionOpenPayload = {
      registerId: openingForm.registerId,
      openingAmount: openingForm.openingAmount,
      details: openingForm.details,
    };

    await openSession(payload);
  };


  /**
 * 📍 Obtener sesión activa
 */
  const getActiveSession = async () => {

    try {

      showLoadingIndicator();
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await CashSessionService.getActiveSession();
      console.log(response);

      const { statusCode, data } = response;

      if (statusCode === 200) {

        dispatch(setActiveSession(data));

      }

    } catch (error) {

      handle(error);

    } finally {

      hideLoadingIndicator();
      dispatch(setLoading(false));

    }

  };


  /**
   * Seleccionar terminal POS
   */
  const setRegister = (registerId: number | null) => {
    dispatch(setOpeningRegister(registerId));
  };

  /**
   * Activar hoja de conteo
   */
  const startCounting = () => {
    dispatch(startCashCount());
  };

  /**
   * Ocultar hoja de conteo
   */
  const stopCounting = () => {
    dispatch(stopCashCount());
  };

  /**
   * Alternar hoja de conteo
   */
  const toggleCounting = () => {
    dispatch(toggleCashCount());
  };

  /**
   * Actualizar denominaciones
   */
  const updateOpeningDetails = (details: CashOpeningDetail[], total: number) => {

    dispatch(setOpeningDetails(details));
    dispatch(setOpeningAmount(total));

  };

  const updateOpeningAmount = (amount: number) => {
    dispatch(setOpeningAmount(amount));
  }

  const clearOpeningDetails = () => {
    dispatch(setOpeningDetails([]));
  };

  const resetOpeningAmountAndDetails = () => {
    dispatch(setOpeningAmount(0));
    dispatch(setOpeningDetails([]));
  };

  const clearActiveSession = () => {
    dispatch(clearSession());
  };


  return {

    /**
     * estados
     */
    activeSession,
    loading,
    error,
    isCountingCash,
    openingForm,

    /**
     * acciones
     */
    getSessionStatus,
    openSession,
    submitOpeningSession,
    getActiveSession,
    clearActiveSession,
    setRegister,
    startCounting,
    stopCounting,
    toggleCounting,
    updateOpeningDetails,
    updateOpeningAmount,
    clearOpeningDetails,
    resetOpeningAmountAndDetails

  };

};
